import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { AccountService } from '../../../../../services/account/account.service';
import { ListResponse } from '../../../../../models/list-response';
import { EventType } from '../../../../../models/event-type';
import { EventTypeService } from '../../../../../services/event-type/event-type.service';
import { batchAll, getISO8601DateString, timeStringFromISO8601 } from '../../../../../utils';
import { OrganizationConfigService } from '../../../../../services/organization-config/organization-config.service';
import { ScheduledEvent } from '../../../../../models/scheduled-event';
import { Event } from '../../../../../models/event';
import { ScheduledEventService } from '../../../../../services/scheduled-event/scheduled-event.service';
import { EventService } from '../../../../../services/event/event.service';

@Component({
  selector: 'app-bulk-create-booking',
  templateUrl: './bulk-create-booking.component.html',
  styleUrls: ['./bulk-create-booking.component.css']
})
export class BulkCreateBookingComponent implements OnInit {

  public customerIds: number[];
  public customerNames: {[key: number]: string};
  // This prevents the count from displaying incorrectly while loading.
  public customerIdCount: number;

  public eventTypeList: ListResponse<EventType>;
  public selectedEventType: EventType = null;
  public selectedDate: string;
  public scheduledEventsForDate: ScheduledEvent[] = [];
  public selectedEvent: Event = null;
  public sendConfirmationEmails = false;

  // Used to display a progress bar.
  public requestsCompleted = 0;

  public serverError: number = null;

  public loadingEventTypes = false;
  public loadingEventsForDate = false;
  public loadingSelectedEvent = false;
  public loadingSubmit = false;

  public errors: any = {};
  public errorMessage = '';

  public timeStringFromISO8601 = timeStringFromISO8601;

  // The number of loadEventsForDate calls. Used to ensure that only the most
  // recent result is displayed, in the event of concurrent requests (due to,
  // e.g., rapid pagination).
  private loadEventsForDateRequestCount = 0;

  constructor(
    public accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private router: Router,
    private eventTypeService: EventTypeService,
    private scheduledEventService: ScheduledEventService,
    private eventService: EventService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.selectedDate = getISO8601DateString(new Date());

    // The customer IDs and names should have been placed in the state by the
    // customers component. If they are missing it is likely because the user
    // navigated directly to this URL. In this case, we redirect back to the
    // customers list.
    const missingRequiredState = (
      !history.state ||
      !history.state.customerIds ||
      !history.state.customerNames
    );
    if (missingRequiredState) {
      this.router.navigate(['/customers']);

      // Set the event types loading state so that Angular does not attempt to
      // render them while navigating to the customers list.
      this.loadingEventTypes = true;

      return;
    }

    this.customerIds = history.state.customerIds;
    this.customerNames = history.state.customerNames;
    this.customerIdCount = this.customerIds.length;

    this.loadEventTypes();
  }

  public loadEventsForDate() {
    this.scheduledEventsForDate = [];

    if (!this.selectedEventType && !this.selectedDate) { return; }

    this.loadingEventsForDate = true;

    // Increment the request count and save a local copy.
    this.loadEventsForDateRequestCount++;
    const thisRequestCount = this.loadEventsForDateRequestCount;

    this.scheduledEventService
      .getScheduledEvents(
        this.selectedDate,
        this.selectedEventType.id,
        false
      )
      .then(scheduledEvents => {
        // Only update the events list if there has not been a more recent
        // request initiated.
        if (thisRequestCount !== this.loadEventsForDateRequestCount) {
          return;
        }

        this.scheduledEventsForDate = scheduledEvents;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        if (thisRequestCount === this.loadEventsForDateRequestCount) {
          this.loadingEventsForDate = false;
        }
      });
  }

  public loadSelectedEvent(selectedEventId: number) {
    this.loadingSelectedEvent = true;

    this.eventService
      .getEvent(selectedEventId)
      .then(event => {
        this.selectedEvent = event;
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSelectedEvent = false;
      });
  }

  public handleSubmit() {
    if (this.isLoading()){ return; }
    if (!this.allInputsSelected()) { return; }

    this.errorMessage = '';
    this.errors = {};

    this.loadingSubmit = true;

    this.requestsCompleted = 0;
    let successfulCount = 0;
    const failedNames = [];
    // The server's refusal reasons, keyed to the customers they apply to:
    // reasons differ per customer (PRO-1457), and the server's sentence is
    // written for a single participant.
    const failedNamesByReason = new Map<string, string[]>();

    const customerCreditSubmitHandler = (customerId: number): Promise<void> => {
      return this.scheduledEventService
        .bookCustomerOntoEventById(
          this.selectedEvent.id,
          customerId,
          this.sendConfirmationEmails
        )
        .then(() => { successfulCount++; })
        .catch(error => {
          if (error.status === 403) {
            this.errorMessage = 'You do not have permission to create bookings for this event.';
          } else if (error.status >= 500) {
            this.serverError = error.status;
          } else {
            failedNames.push(this.customerNames[customerId]);
            // Only a 400 carries a refusal message. Anything else here (a 404
            // for a removed customer, a dropped connection) has a body that is
            // an HTML error page or a ProgressEvent, which must not be pasted
            // into staff-facing copy.
            if ((error.status === 400) && (typeof error.error === 'string')) {
              const named = failedNamesByReason.get(error.error) || [];
              named.push(this.customerNames[customerId]);
              failedNamesByReason.set(error.error, named);
            }
          }
        })
        .finally(() => { this.requestsCompleted++; });
    };

    batchAll(
      1,
      this.customerIds,
      customerCreditSubmitHandler
    ).then(() => {
      if (this.serverError) {
        return;
      }

      const reasonsText = this.buildFailureReasonsText(failedNamesByReason);

      if (successfulCount === 0) {
        if (this.errorMessage === '') {
          this.errorMessage = (reasonsText === '')
            ? 'Unable to create bookings. Please check that the customers ' +
              'are not already booked onto the event.'
            : 'Unable to create bookings. ' + reasonsText;
        }
        return;
      }

      let successMessage = (
        'Successfully booked ' + successfulCount.toString() + ' customers.'
      );
      if (failedNames.length > 0) {
        // The reason lines already name everyone they cover, so only the
        // customers with no reason to report (a 404, a dropped connection)
        // still need listing — otherwise every failed customer would appear
        // twice in one sentence.
        const namedInReasons = new Set<string>();
        failedNamesByReason.forEach(names => {
          names.forEach(name => namedInReasons.add(name));
        });
        const unexplainedNames = failedNames
          .filter(name => !namedInReasons.has(name));

        if (unexplainedNames.length > 0) {
          successMessage += ' Unable to book the following customer';
          if (unexplainedNames.length > 1) {
            successMessage += 's';
          }
          successMessage += ': ' + unexplainedNames.join(', ') + '.';
          if (reasonsText === '') {
            successMessage +=
              ' They were likely already booked onto the event.';
          }
        }
        if (reasonsText !== '') {
          successMessage += ' ' + reasonsText;
        }
      }

      this.router.navigate(
        ['/customers'],
        {
          state: {
            feedback: {
              text: successMessage,
              status: 'success'
            }
          }
        }
      );
    }).finally(() => { this.loadingSubmit = false; });
  }

  /**
   * Render the refusal reasons the server gave, each prefixed with the
   * customers it applies to. The server writes these for one participant
   * ("This participant has..."), so a bare list of them in a bulk summary
   * would not say who any of them is about.
   */
  private buildFailureReasonsText(failedNamesByReason: Map<string, string[]>): string {
    const parts: string[] = [];

    failedNamesByReason.forEach((names, reason) => {
      parts.push(names.join(', ') + ': ' + reason);
    });

    return parts.join(' ');
  }

  public isLoading() {
    return (
      this.loadingEventTypes ||
      this.loadingEventsForDate ||
      this.loadingSelectedEvent ||
      this.loadingSubmit
    );
  }

  public allInputsSelected() {
    return (
      (this.selectedEventType !== null) &&
      (this.selectedDate !== null) &&
      (this.selectedEvent !== null)
    );
  }

  public getProgressPercentageStr() {
    return Math.ceil((this.requestsCompleted / this.customerIds.length) * 100).toString();
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {}
      )
      .then(response => {
        this.eventTypeList = response;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => {
        this.loadingEventTypes = false;
      });
  }

}
